import { useEffect, useMemo, useState } from 'react';
import { IconAlertTriangle, IconArrowLeft, IconBell, IconCalendar, IconCheck, IconChevronDown, IconDots, IconDownload, IconRefresh, IconUserCheck } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Skeleton, StateView, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import './payroll.i18n';
import { AllowTab, LogTab, ParamTab } from './OtherTabs';
import { StdTab } from './StdTab';
import { SlipPage } from './SlipPage';
import { payrollScreenState, usePayrollAccess } from './access';
import { LOG_PERIODS, PAYROLL_WARNS, PAY_PERIODS, PAY_STEPS, byDept, calcAll, fmt, nf } from './payrollData';
import s from './payroll.module.css';

// Mint-Payroll (1536 · 1920): Lương — tab Bảng lương kỳ: luồng duyệt · quỹ lương theo phòng · cơ cấu Gross · bảng 14 cột.
// TIỀN → sau quyền `money`. Chỉ xem; nút ghi báo toast.

type Tab = 'run' | 'std' | 'param' | 'allow' | 'log';
const TABS: readonly (readonly [Tab, string, string])[] = [
  ['run', 'Bảng lương kỳ', '3'], ['std', 'Bảng lương chuẩn', '14'], ['param', 'Tham số & biểu thuế', '3'], ['allow', 'Phụ cấp · KPI · chuyên cần', ''], ['log', 'Nhật ký gửi phiếu', ''],
];
const STEP = 2; // kỳ 10/2026 đã tính, chờ KTT kiểm
const CIRC = 2 * Math.PI * 52;

export function PayrollScreen() {
  const t = useT();
  const toast = useToast();
  const access = usePayrollAccess();
  const [tab, setTab] = useState<Tab>('run');
  const [dense, setDense] = useState(false);
  const [per, setPer] = useState(0);
  const [slip, setSlip] = useState<string | null>(() => (typeof location === 'undefined' ? null : new URLSearchParams(location.search).get('slip')));
  const [ktt, setKtt] = useState<'vacant' | 'acting'>('vacant');
  const [wide, setWide] = useState(() => typeof matchMedia !== 'undefined' && matchMedia('(min-width: 1800px)').matches);
  useEffect(() => {
    const mq = matchMedia('(min-width: 1800px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const state = payrollScreenState(access);
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });

  const rows = useMemo(() => calcAll(), []);
  const sum = (f: (c: (typeof rows)[number]) => number) => rows.reduce((a, c) => a + f(c), 0);
  const totG = sum((c) => c.gross);
  const totN = sum((c) => c.net);
  const depts = byDept(rows);
  const dTot = depts.reduce((a, d) => a + Math.max(d.v, 0), 0) || 1;
  const parts = [
    ['Lương chính', sum((c) => c.main), 'var(--d-blue)'],
    ['Phụ cấp · trách nhiệm', sum((c) => c.alw + c.resp), 'var(--d-blue)'],
    ['OT', sum((c) => c.ot), 'var(--d-amber)'],
    ['KPI', sum((c) => c.kpi), 'var(--d-green)'],
    ['Thưởng · chuyên cần', sum((c) => c.bonus), 'var(--d-violet)'],
  ] as const;
  const totals = [sum((c) => c.main), sum((c) => c.alw + c.resp), sum((c) => c.leg), sum((c) => c.ot), sum((c) => c.kpi), sum((c) => c.bonus), totG] as const;
  let acc = 0;
  const kpis: readonly (readonly [string, string, string])[] = [
    ['Tổng Gross', nf(totG), 'tr ₫'], ['Thực nhận', nf(totN), 'tr ₫'], ['BH NLĐ + TNCN', nf(sum((c) => c.ins + c.pit)), 'tr ₫'], ['Bước hiện tại', PAY_STEPS[STEP]![0], PAY_STEPS[STEP]![1]],
  ];

  if (slip && access.money) return <SlipPage user={slip} onBack={() => setSlip(null)} />;

  return (
    <Page
      kind="table"
      dock={wide ? (
        <div className={s.dock}>
          <h2>{t('pr.review')}</h2>
          {access.money ? (
            <>
              <b>{t('pr.preCheck', { p: '10/2026' })}</b>
              {PAYROLL_WARNS.map((w) => (
                <button key={w.k} type="button" className={`${s.dw} ${w.tone === 'err' ? s.werr : ''}`} onClick={() => toast({ msg: 'Phiếu lương chưa mở ở bản này' })}>
                  <b><IconAlertTriangle size={16} aria-hidden="true" />{w.k}</b><span>{w.sub}</span>
                </button>
              ))}
              <b className={s.dh}>{t('pr.kttStep')}</b>
              <div className={s.seg2} role="group" aria-label="Biến thể ghế ACC-CA">
                {([['vacant', 'Ghế trống'], ['acting', 'Acting NhungTT']] as const).map(([k, l]) => (
                  <button key={k} type="button" aria-pressed={ktt === k} onClick={() => setKtt(k)}>{l}</button>
                ))}
              </div>
              <div className={s.note}>{ktt === 'acting' ? 'NhungTT giữ ghế ACC-CA (Acting) 24/Oct–31/Dec/2026.' : 'Ghế ACC-CA trống → bước KTT kiểm lên [BOD-DIR] PHILIP.'}</div>
              <b className={s.dh}>{t('pr.periods')}</b>
              <div className={s.pers}>
                {PAY_PERIODS.map(([k, st, tone], i) => (
                  <button key={k} type="button" data-on={i === 1 ? 1 : undefined} onClick={() => toast({ msg: `Chọn kỳ ${k}` })}>
                    <span>Kỳ {k}</span><span className={`${s.pst} ${s[`t_${tone}`]}`}>{st}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className={s.note}>{t('pr.needPerm')}</div>
          )}
        </div>
      ) : undefined}
    >
      <PageHeader
        title="Lương"
        findHint
        lead={
          <button type="button" className={s.tool} aria-label="Về Nhân sự" title="Về Nhân sự" onClick={() => toast({ msg: 'Nhân sự mở ở vùng Quản trị & HR' })}>
            <IconArrowLeft size={18} aria-hidden="true" />
          </button>
        }
        actions={
          <>
            <div className={s.seg} role="group" aria-label="Mật độ">
              {([['Chuẩn', false], ['Gọn', true]] as const).map(([l, d]) => (
                <button key={l} type="button" aria-pressed={dense === d} onClick={() => setDense(d)}>{l}</button>
              ))}
            </div>
            <HeaderTool label="Thông báo · 4 mới" icon={IconBell} onClick={noWrite} />
            {!wide && <HeaderTool label="Kiểm & duyệt" icon={IconUserCheck} onClick={noWrite} />}
            <HeaderTool label="Thêm thao tác" icon={IconDots} onClick={noWrite} />
            <Button variant="primary" onClick={noWrite}>{t('pr.kttConfirm')}</Button>
          </>
        }
      />
      <div className={s.page}>
        <div className={s.tabs} role="tablist" aria-label="Lương">
          {TABS.map(([k, l, n]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
              {l}{n && <i className={k === 'std' ? '' : s.warnN}>{n}</i>}
            </button>
          ))}
        </div>
        <StateView
          state={state}
          title={state === 'empty' ? 'Chưa có kỳ lương nào' : undefined}
          desc={state === 'empty' ? 'Cần chốt công tháng và có bảng lương chuẩn trước khi tính kỳ đầu tiên.' : state === 'noperm' ? 'Bảng lương chỉ hiện cho người có quyền nhóm lương.' : undefined}
          skeleton={<Skeleton rows={7} />}
        >
          {tab === 'std' ? (
            <StdTab dense={dense} />
          ) : tab === 'param' ? (
            <ParamTab dense={dense} />
          ) : tab === 'allow' ? (
            <AllowTab dense={dense} />
          ) : tab === 'log' ? (
            <>
              <section className={s.flow} aria-label="Kỳ lương">
                <button type="button" className={s.per} title="Chọn kỳ lương" onClick={() => setPer((per + 1) % LOG_PERIODS.length)}>
                  <IconCalendar size={16} aria-hidden="true" />Kỳ {LOG_PERIODS[per]}<IconChevronDown size={14} aria-hidden="true" />
                </button>
              </section>
              <LogTab per={LOG_PERIODS[per]!} dense={dense} />
            </>
          ) : (
            <>
              <div className={s.kpis}>
                {kpis.map(([k, v, u]) => (
                  <div key={k} className={s.kpi}><div>{k}</div><div>{v} <small>{u}</small></div></div>
                ))}
              </div>
              <section className={s.flow} aria-label="Kỳ lương và luồng duyệt">
                <button type="button" className={s.per} title="Chọn kỳ lương" onClick={() => toast({ msg: 'Chọn kỳ: 11/2026 … 07/2026' })}>
                  <IconCalendar size={16} aria-hidden="true" />{t('pr.period', { p: '10/2026' })}<IconChevronDown size={14} aria-hidden="true" />
                </button>
                <ol className={s.steps}>
                  {PAY_STEPS.map(([k, who], i) => (
                    <li key={k} title={who} data-on={i === STEP ? 1 : undefined} data-done={i < STEP ? 1 : undefined}>
                      <span className={s.dot}>{i < STEP ? <IconCheck size={12} aria-hidden="true" /> : i + 1}</span>{k}
                    </li>
                  ))}
                </ol>
              </section>
              <div className={s.runbar}>
                <span className={s.warnChip}><IconAlertTriangle size={14} aria-hidden="true" />{PAYROLL_WARNS.length} cảnh báo trước khi duyệt</span>
                <span>Bảng công 10/2026 đã chốt · {rows.length} phiếu · Calculated</span>
                <button type="button" className={s.tool} aria-label="Tính lại cả kỳ" title="Tính lại cả kỳ" onClick={noWrite}><IconRefresh size={16} aria-hidden="true" /></button>
                <button type="button" className={s.tool} aria-label="Xuất Excel" title="Xuất Excel" onClick={noWrite}><IconDownload size={16} aria-hidden="true" /></button>
              </div>
              <div className={s.charts}>
                <section className={s.card} aria-label="Quỹ lương theo phòng">
                  <h2>{t('pr.fund')}</h2><small className={s.unit}>{t('pr.unitPer', { p: '10/2026' })}</small>
                  <div className={s.dn}>
                    <svg viewBox="0 0 120 120" width="104" height="104" role="img" aria-label={`Quỹ lương Gross theo phòng (tr ₫): ${depts.map((d) => `${d.k} ${nf(d.v)}`).join(', ')}`}>
                      {depts.map((d) => {
                        const L = (Math.max(d.v, 0) / dTot) * CIRC;
                        const el = <circle key={d.k} cx="60" cy="60" r="52" fill="none" stroke={d.c} strokeWidth="14" strokeDasharray={`${Math.max(L - 3, 0)} ${CIRC - L + 3}`} strokeDashoffset={-acc} transform="rotate(-90 60 60)" />;
                        acc += L;
                        return el;
                      })}
                      <text x="60" y="64" textAnchor="middle" className={s.dnT}>{nf(totG)}</text>
                    </svg>
                    <ul>{depts.map((d) => <li key={d.k}><i style={{ background: d.c }} />{d.k}<span>{d.n} người</span><b>{nf(d.v)}</b><em>{Math.round((d.v / dTot) * 100)}%</em></li>)}</ul>
                  </div>
                </section>
                <section className={s.card} aria-label="Cơ cấu Gross">
                  <h2>{t('pr.struct')}</h2><small className={s.unit}>tr ₫</small>
                  <div className={s.bar} role="img" aria-label={`Cơ cấu Gross (tr ₫): ${parts.map((p) => `${p[0]} ${nf(p[1])}`).join(', ')}`}>
                    {parts.map(([k, v, c]) => <span key={k} style={{ flex: Math.max(v, 0.001), background: c }} />)}
                  </div>
                  <ul className={s.pl}>{parts.map(([k, v, c]) => <li key={k}><i style={{ background: c }} />{k}<b>{nf(v)}</b></li>)}</ul>
                  <div className={s.net}>Gross <b>{nf(totG)}</b> {t('pr.toDed')} <b>{nf(totG - totN)}</b> {t('pr.toNet')} <b>{nf(totN)}</b></div>
                </section>
              </div>
              <section className={s.card} aria-label="Bảng lương kỳ">
                <div className={s.scroll}>
                  <table className={s.tbl} data-dense={dense ? 1 : undefined}>
                    <thead>
                      <tr>
                        {['Người · ghế', 'Công', 'Lương chính', 'Phụ cấp', 'Lương chặng', 'OT', 'KPI', 'Thưởng', 'Gross', 'BH NLĐ', 'TNCN', 'Tạm ứng · khác', 'Thực nhận', 'Trạng thái'].map((h, i) => (
                          <th key={h} scope="col" className={i && i < 13 ? s.r : undefined}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((c) => (
                        <tr key={c.u} tabIndex={0} onClick={() => setSlip(c.u)} onKeyDown={(e) => e.key === 'Enter' && setSlip(c.u)}>
                          <td><b>{c.u}</b><span>{c.seat}</span></td>
                          <td className={`${s.r} ${c.cong ? '' : s.errc}`}>{nf(c.cong).replace(',0', '')}/{c.std}</td>
                          {[[c.main], [c.alw + c.resp], [c.leg], [c.ot], [c.kpi], [c.bonus], [c.gross, 0, 1], [c.ins, 1], [c.pit, 1], [c.adv + c.oth, 1]].map(([v, neg, b], i) => (
                            <td key={i} className={`${s.r} ${neg ? s.tx2 : ''}`} style={{ fontWeight: b ? 600 : undefined }}>{fmt(v!, !!neg)}</td>
                          ))}
                          <td className={s.r}><b>{nf(c.net)}</b></td>
                          <td><span className={s.chip}>Calculated</span></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Tổng · {rows.length} người</td><td />
                        {totals.map((v, i) => <td key={i} className={s.r}>{fmt(v)}</td>)}
                        <td className={s.r}>{fmt(sum((c) => c.ins), true)}</td><td className={s.r}>{fmt(sum((c) => c.pit), true)}</td><td className={s.r}>{fmt(sum((c) => c.adv + c.oth), true)}</td>
                        <td className={s.r}>{nf(totN)}</td><td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </section>
            </>
          )}
        </StateView>
      </div>
    </Page>
  );
}

